import { useState, useCallback, useEffect, useRef } from 'react';
import superjson from 'superjson';

const CHAT_MESSAGES_KEY = 'taskingbot-chat-messages';
const MAX_PERSISTED_MESSAGES = 200;

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  participantName?: string;
}

function loadPersistedMessages(): Message[] {
  try {
    const raw = localStorage.getItem(CHAT_MESSAGES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) }));
  } catch {
    return [];
  }
}

function persistMessages(messages: Message[]) {
  try {
    // Only keep the most recent messages to avoid bloating localStorage
    const toSave = messages.slice(-MAX_PERSISTED_MESSAGES);
    localStorage.setItem(CHAT_MESSAGES_KEY, JSON.stringify(toSave));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

export function useTaskingBot() {
  const [messages, setMessages] = useState<Message[]>(() => loadPersistedMessages());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const skipPersistRef = useRef(false);
  const lastResponseRef = useRef<string | null>(null);

  // Persist messages whenever they change
  useEffect(() => {
    if (skipPersistRef.current) {
      skipPersistRef.current = false;
      return;
    }
    persistMessages(messages);
  }, [messages]);

  const sendMessage = useCallback(
    async (
      userMessage: string,
      multiAgent?: 'parallel' | 'sequential',
      participants?: string[],
      assistantPreface?: string,
    ) => {
      if (!userMessage.trim() || isLoading) return;

      setError(null);

      // Add user message
      const userMsg: Message = {
        id: `user_${Date.now()}`,
        role: 'user',
        content: userMessage,
        timestamp: new Date(),
      };
      setMessages((prev) => {
        if (!assistantPreface) {
          return [...prev, userMsg];
        }

        return [
          ...prev,
          userMsg,
          {
            id: `assistant_preface_${Date.now()}`,
            role: 'assistant' as const,
            content: assistantPreface,
            timestamp: new Date(),
          },
        ];
      });
      setIsLoading(true);

      try {
        // Convert messages to history format for API
        const history = messages.map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        }));

        // Call TaskingBot API with streaming
        const response = await fetch('/_api/taskingbot/chat', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: superjson.stringify({
            message: userMessage,
            history,
            ...(multiAgent && { multiAgent }),
            ...(participants && participants.length > 0 && { participants }),
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Error: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response from TaskingBot');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let assistantContent = '';
        const assistantId = `assistant_${Date.now()}`;

        // Stream response
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          assistantContent += chunk;

          // Update message in real-time
          setMessages((prev) => {
            const lastMsg = prev[prev.length - 1];
            if (lastMsg?.id === assistantId) {
              return [...prev.slice(0, -1), { ...lastMsg, content: assistantContent }];
            } else {
              return [
                ...prev,
                {
                  id: assistantId,
                  role: 'assistant' as const,
                  content: assistantContent,
                  timestamp: new Date(),
                },
              ];
            }
          });
        }

        // Signal that the last response is available for auto-continuation checks
        lastResponseRef.current = assistantContent;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);

        const errorMsg: Message = {
          id: `error_${Date.now()}`,
          role: 'assistant',
          content: `❌ Error: ${errorMessage}`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, messages],
  );

  const addSystemMessage = useCallback((content: string, participantName?: string) => {
    const systemMsg: Message = {
      id: `system_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: 'system',
      content,
      timestamp: new Date(),
      ...(participantName && { participantName }),
    };
    setMessages((prev) => [...prev, systemMsg]);
  }, []);

  const addAssistantMessage = useCallback((content: string) => {
    const assistantMsg: Message = {
      id: `assistant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: 'assistant',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, assistantMsg]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    try { localStorage.removeItem(CHAT_MESSAGES_KEY); } catch {}
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    addSystemMessage,
    addAssistantMessage,
    lastResponseRef,
  };
}
